import {parseAxiosPromise} from '@/axiosInstance'
import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'
import {useInputSearch} from '@/hooks/useInputSearch'
import {httpGetProvinces, HttpGetProvincesOptions} from './address.http'
import {PaginationResponse, Province} from '@/types'
import {z} from 'zod'

export const HttpGetProvincesResponse = z.object({
    data: Province.array(),
    meta: PaginationResponse
})
export type HttpGetProvincesResponse = z.infer<typeof HttpGetProvincesResponse>

export const useGetProvinces = (
    promiseArgs: Omit<HttpGetProvincesOptions, 'page'>,
    options?: Pick<UseInfiniteQueryOptions<HttpGetProvincesResponse>, 'enabled'>
) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.PROVINCES, searchValue, promiseArgs],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetProvinces({
                    page: pageParam,
                    search: searchValue || promiseArgs.search,
                    limit: promiseArgs.limit
                }),
                responseShape: HttpGetProvincesResponse,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
